import { Client } from "@stomp/stompjs";

import { getAuthToken, logout } from "../auth/authService";
import { isSessionValid } from "../auth/sessionManager";

const WS_URL = process.env.REACT_APP_WS_URL;

const MAX_RECONNECT_ATTEMPTS = 10;

class WebSocketManager {
  constructor() {
    if (!WebSocketManager.instance) {

      this.client = null;

      this.subscriptions = new Map();
      this.pendingSubscriptions = [];

      this.isConnected = false;
      this.reconnectAttempts = 0;

      WebSocketManager.instance = this;
    }
    return WebSocketManager.instance;
  }

  connect() {

    if (!isSessionValid()) {
      logout();
      return;
    }

    if (this.client?.active) {
      return this.client;
    }

    console.log("[WS] Connecting to", WS_URL);
    this.client = new Client({

      brokerURL: WS_URL,

      reconnectDelay: 5000,

      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,

      beforeConnect: () => {

        const token = getAuthToken();

        this.client.connectHeaders = {
          Authorization: `Bearer ${token}`
        };

      },

      onConnect: () => {
        console.log("[WS] Connected");

        this.isConnected = true;
        this.reconnectAttempts = 0;

        this._renewSubscriptions();
        this._processPendingSubscriptions();

      },

      onStompError: (frame) => {
        console.error("[WS] Broker error:", frame.headers["message"]);
        console.error("[WS] Details:", frame.body);

      },

      onWebSocketClose: () => {
        console.warn("[WS] Connection closed");
        this.isConnected = false;
        if (this.reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
          this.reconnectAttempts++;
          console.log(`[WS] Reconnect attempt ${this.reconnectAttempts}`);
        } else {
          console.error("[WS] Max reconnect attempts reached");
        }
      },

      debug: process.env.NODE_ENV === "development"
        ? (str) => console.log("[WS]", str)
        : () => { }

    });

    this.client.activate();

    return this.client;

  }

  _processPendingSubscriptions() {
    if (!this.pendingSubscriptions.length) return;

    const toProcess = [...this.pendingSubscriptions];
    this.pendingSubscriptions = [];

    toProcess.forEach(({ topic, callback }) => {
      this._subscribe(topic, callback);
    });

  }

  _renewSubscriptions() {

    this.subscriptions.forEach((data, topic) => {
      try {
        const sub = this.client.subscribe(topic, data.callback);

        this.subscriptions.set(topic, {
          ...data,
          subscription: sub
        });

        console.log(`[WS] Renewed subscription: ${topic}`);
      } catch (e) {
        console.warn(`[WS] Failed to renew subscription: ${topic}`, e);
      }
    });
  }

  _subscribe(topic, callback) {
    if (!this.client || !this.isConnected) return;

    const existing = this.subscriptions.get(topic);
    if (existing?.subscription) {
      try {
        existing.subscription.unsubscribe();
      } catch (e) {
        console.warn(`[WS] Failed to unsubscribe existing ${topic}`);
      }
    }

    const wrapper = (message) => {
      try {
        const payload = JSON.parse(message.body);
        callback(payload);
      } catch (e) {
        callback(message.body);
      }
    };

    const subscription = this.client.subscribe(topic, wrapper);

    this.subscriptions.set(topic, {
      topic,
      callback: wrapper,
      subscription
    });

    console.log(`[WS] Subscribed to ${topic}`);

  }

  subscribe(topic, callback) {
    this.connect();

    if (!this.isConnected) {
      console.log(`[WS] Queueing subscription for ${topic}`);

      this.pendingSubscriptions.push({
        topic,
        callback
      });

      return;

    }

    this._subscribe(topic, callback);

  }

  unsubscribe(topic) {
    const data = this.subscriptions.get(topic);
    if (data?.subscription) {
      try {
        data.subscription.unsubscribe();
      } catch (e) {
        console.warn(`[WS] Failed unsubscribe ${topic}`);
      }
    }
    this.subscriptions.delete(topic);

    this.pendingSubscriptions =
      this.pendingSubscriptions.filter(s => s.topic !== topic);

  }

  disconnect() {
    if (!this.client) return;
    try {
      this.client.deactivate();
    } catch (e) {
      console.warn("[WS] Error during deactivate", e);
    }

    this.client = null;

    this.isConnected = false;

    this.subscriptions.clear();
    this.pendingSubscriptions = [];

    this.reconnectAttempts = 0;

    console.log("[WS] Manager deactivated");

  }
}

const instance = new WebSocketManager();
export default instance;